import React, { useState, useEffect, useRef } from 'react';
import { Check, AlertCircle } from 'react-feather';

/**
 * InlineInput — ghost-to-active table input with debounced auto-save.
 * Numeric fields render in JetBrains Mono. An orange dot signals a pending
 * save; changes commit on a 1.5s debounce and immediately on blur.
 */
function InlineInput({
  value,
  onChange,
  type = 'text',
  placeholder = '',
  rowActive = false,
  mono = false,
  disabled = false,
  debounceMs = 1500,
  step,
  suffix,
  ariaLabel,
}) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value ?? '');
  const [unsaved, setUnsaved] = useState(false);
  const [saveState, setSaveState] = useState('idle');
  const tmr = useRef(null);
  const statusTmr = useRef(null);
  const lastSubmitted = useRef(value ?? '');

  useEffect(() => {
    const next = value ?? '';
    setLocal(next);
    lastSubmitted.current = next;
  }, [value]);
  useEffect(() => () => {
    clearTimeout(tmr.current);
    clearTimeout(statusTmr.current);
  }, []);

  const settleStatus = (state) => {
    setSaveState(state);
    clearTimeout(statusTmr.current);
    statusTmr.current = setTimeout(() => setSaveState('idle'), state === 'saved' ? 1400 : 2200);
  };

  const commit = async (v) => {
    clearTimeout(tmr.current);
    if (v === (value ?? '') || v === lastSubmitted.current) {
      setUnsaved(false);
      return;
    }

    setUnsaved(false);
    setSaveState('saving');
    lastSubmitted.current = v;
    try {
      await onChange(v);
      settleStatus('saved');
    } catch (error) {
      lastSubmitted.current = value ?? '';
      settleStatus('error');
    }
  };

  const change = (e) => {
    const v = e.target.value;
    setLocal(v); setUnsaved(true); setSaveState('idle');
    clearTimeout(tmr.current);
    tmr.current = setTimeout(() => commit(v), debounceMs);
  };

  const show = (rowActive || editing) && !disabled;

  return (
    <div style={{ width: '100%', padding: '0 4px', position: 'relative' }}>
      <input
        type={type}
        value={local}
        step={step}
        placeholder={show ? placeholder : ''}
        aria-label={ariaLabel}
        disabled={disabled}
        onChange={change}
        onFocus={() => setEditing(true)}
        onBlur={() => { setEditing(false); commit(local); }}
        style={{
          display: 'block', width: '100%', height: 30, boxSizing: 'border-box', borderRadius: 6, fontSize: 13, outline: 'none',
          padding: suffix ? '0 20px 0 7px' : '0 7px',
          border: `1px solid ${editing ? 'var(--lodge-orange-400)' : show ? 'var(--lodge-onyx-200)' : 'transparent'}`,
          background: 'transparent',
          boxShadow: editing ? 'var(--focus)' : 'none',
          color: (local !== '' && local != null) ? 'var(--lodge-onyx-900)' : 'var(--lodge-onyx-400)',
          fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)',
          transition: 'border-color .1s, box-shadow .1s',
        }}
      />
      {suffix && (
        <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--lodge-onyx-500)', pointerEvents: 'none' }}>{suffix}</span>
      )}
      {unsaved && saveState === 'idle' && !suffix && (
        <span style={{ position: 'absolute', top: 7, right: 12, width: 5, height: 5, borderRadius: '50%', background: 'var(--lodge-orange-400)', pointerEvents: 'none' }} />
      )}
      {saveState === 'saving' && !suffix && (
        <span style={{ position: 'absolute', top: 7, right: 12, width: 5, height: 5, borderRadius: '50%', background: 'var(--lodge-orange-400)', pointerEvents: 'none' }} />
      )}
      {saveState === 'saved' && !suffix && (
        <Check size={12} color="var(--color-success)" style={{ position: 'absolute', top: 7, right: 8, pointerEvents: 'none' }} />
      )}
      {saveState === 'error' && !suffix && (
        <AlertCircle size={12} color="var(--color-error)" style={{ position: 'absolute', top: 7, right: 8, pointerEvents: 'none' }} />
      )}
    </div>
  );
}

export default InlineInput;
